/**
 * Which symbol the card itself carries.
 *
 * The dialog and the downloads always encode the full vCard. The on-card
 * symbol has a fixed amount of room, and Marketing asked for it to sit
 * smaller on the face (October 2026), so the card encodes at error-correction
 * L: a screen is a clean surface, and L needs fewer modules than M for the
 * same vCard, and it encodes a compact form of the vCard (`compactVcard`).
 * The size Marketing marked on the 560 px card is 160 px. The symbol may
 * take up to 57 modules (version 10, 271 bytes at L), so a US card keeps its
 * full address. From DPR 1.25 up the modules are whole device pixels inside
 * that box (137 to 152 px); on a screen at 100 % scaling uneven 2/3 px
 * modules decoded measurably worse, so there the symbol keeps whole 3 px
 * modules and grows to 171 px (Timo, 15 October 2026). The card tries, in order:
 *
 *   1. the compact vCard,
 *   2. the compact vCard without the free-text street lines of ADR (city,
 *      state, postcode and country stay),
 *   3. the compact vCard without the postal address (name, company, title,
 *      phones and e-mail stay; this is the normal case).
 *
 * The first candidate of at most 57 modules wins; failing that the smallest.
 */

import { type EccLevel, encodeQr, type QrMatrix } from "@/lib/qr";

export interface CardSymbol {
  /** The text that was encoded (full or trimmed vCard). */
  value: string;
  ecc: EccLevel;
  matrix: QrMatrix;
  /** True when the street lines were dropped to keep the symbol scannable. */
  trimmed: boolean;
}

const CARD_TARGET_VERSION = 10; // 57 modules: inside Marketing's 160 px from DPR 1.25 up, 171 px at DPR 1
const ACCEPTABLE_MAX_VERSION = 17; // 85 modules

/**
 * The symbol the large QR dialog shows: always the full vCard. Error
 * correction M as long as the symbol stays at or below version 17 (85
 * modules); the longest vCards (CJK names, bilingual streets) fall back to L,
 * which drops a 97-module symbol to 89 and keeps the modules larger in the
 * same box. A phone screen is a clean surface, so L is enough there; the
 * downloads keep M for print.
 */
export function chooseDialogSymbol(vcard: string): Pick<CardSymbol, "value" | "ecc" | "matrix"> {
  const medium = encodeQr(vcard, "M");
  if (medium.version <= ACCEPTABLE_MAX_VERSION) {
    return { value: vcard, ecc: "M", matrix: medium };
  }
  const low = encodeQr(vcard, "L");
  return low.size < medium.size
    ? { value: vcard, ecc: "L", matrix: low }
    : { value: vcard, ecc: "M", matrix: medium };
}

export function chooseCardSymbol(vcard: string): CardSymbol {
  const compact = compactVcard(vcard);
  const candidates: Array<[string, EccLevel, boolean]> = [[compact, "L", false]];
  for (const shorter of [vcardWithoutStreet(compact), vcardWithoutAddress(compact)]) {
    if (!candidates.some(([value]) => value === shorter)) {
      candidates.push([shorter, "L", true]);
    }
  }
  const encoded: CardSymbol[] = candidates.map(([value, ecc, wasTrimmed]) => ({
    value,
    ecc,
    matrix: encodeQr(value, ecc),
    trimmed: wasTrimmed,
  }));
  return (
    encoded.find((c) => c.matrix.version <= CARD_TARGET_VERSION) ??
    encoded.reduce((best, c) => (c.matrix.size < best.matrix.size ? c : best))
  );
}

/** The same vCard without its ADR lines. */
export function vcardWithoutAddress(vcard: string): string {
  const lines = vcard.replace(/\r?\n[ \t]/g, "").split(/\r?\n/);
  const kept = lines.filter((line) => !/^ADR(;|:)/i.test(line));
  return kept.length === lines.length ? vcard : kept.map(fold).join("\r\n");
}

const MAX_LINE_OCTETS = 75;

/**
 * The vCard as the on-card symbol carries it: the same contact, written
 * shorter, so a typical card needs 57 modules instead of 65. Dropped are the
 * property parameters a phone does not need to file the value (EMAIL and ADR
 * types, VOICE), the spaces inside phone numbers, the empty tail of N, the
 * organisational units after the company name and the URL line (the brand
 * website, printed on the card face). The QR dialog, "Save contact" and the
 * downloads keep the full vCard.
 */
export function compactVcard(vcard: string): string {
  const lines = vcard.replace(/\r?\n[ \t]/g, "").split(/\r?\n/);
  const result: string[] = [];
  for (const line of lines) {
    const colon = line.indexOf(":");
    const name = (colon < 0 ? line : line.slice(0, colon)).toUpperCase();
    const value = colon < 0 ? "" : line.slice(colon + 1);
    const property = name.split(";")[0];
    if (property === "URL") {
      continue;
    }
    if (property === "EMAIL" || property === "ADR") {
      result.push(`${property}:${value}`);
    } else if (property === "TEL") {
      const params = name.replace(/,VOICE\b|\bVOICE,/, "");
      result.push(`${params}:${value.replace(/\s+/g, "")}`);
    } else if (property === "N") {
      result.push(`N:${value.replace(/;+$/, "")}`);
    } else if (property === "ORG") {
      result.push(`ORG:${splitUnescaped(value, ";")[0] ?? ""}`);
    } else {
      result.push(line);
    }
  }
  return result.map(fold).join("\r\n");
}

/**
 * The same vCard with the street component of every ADR line emptied. The
 * text is unfolded first (RFC 2426 folds at 75 octets) and folded again, so
 * the result is a valid vCard 3.0 whatever the input folding was. A vCard
 * without an ADR line is returned unchanged (same string identity).
 */
export function vcardWithoutStreet(vcard: string): string {
  const unfolded = vcard.replace(/\r?\n[ \t]/g, "");
  const lines = unfolded.split(/\r?\n/);
  let changed = false;
  const result = lines.map((line) => {
    const colon = line.indexOf(":");
    if (colon < 0 || !/^ADR(;|$)/i.test(line.slice(0, colon))) {
      return line;
    }
    const parts = splitUnescaped(line.slice(colon + 1), ";");
    if (parts.length < 3 || parts[2] === "") {
      return line;
    }
    changed = true;
    parts[2] = "";
    return `${line.slice(0, colon + 1)}${parts.join(";")}`;
  });
  if (!changed) {
    return vcard;
  }
  return result.map(fold).join("\r\n");
}

/** Split on `separator` unless it is escaped with a backslash. */
function splitUnescaped(value: string, separator: string): string[] {
  const parts: string[] = [];
  let current = "";
  for (let i = 0; i < value.length; i++) {
    const char = value[i] ?? "";
    if (char === "\\" && i + 1 < value.length) {
      current += char + value[i + 1];
      i += 1;
    } else if (char === separator) {
      parts.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  parts.push(current);
  return parts;
}

/** Fold at 75 octets (UTF-8); continuation lines start with a single space. */
function fold(line: string): string {
  const data = new TextEncoder().encode(line);
  if (data.length <= MAX_LINE_OCTETS) {
    return line;
  }
  const decoder = new TextDecoder();
  const chunks: string[] = [];
  let start = 0;
  let limit = MAX_LINE_OCTETS;
  while (start < data.length) {
    let end = Math.min(start + limit, data.length);
    while (end < data.length && ((data[end] ?? 0) & 0xc0) === 0x80) {
      end -= 1;
    }
    chunks.push(decoder.decode(data.subarray(start, end)));
    start = end;
    limit = MAX_LINE_OCTETS - 1;
  }
  return chunks.join("\r\n ");
}
